import React, { useState, useEffect } from 'react';
import { GetDailyAttendanceSummary } from '../../services/dataService';
import { useNavigate } from 'react-router-dom';

// Utility to format date as YYYY-MM-DD using local time
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function AttendanceCalendar() {
  const [FromDate, setFromDate] = useState('');
  const [ToDate, setToDate] = useState('');
  const [Data, setData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const handleViewMore = (date) => {
    
    // Logic to display detailed information about the selected row
    navigate("/DetailedAttendance", {
      state: {
        // Spread the existing row properties
        date: date?.dateStr, // Add the date property
      },
    });
  };
  const getMonthRange = (monthString) => {
    const [year, month] = monthString.split('-');
    const monthNum = parseInt(month, 10);
    const firstDay = `${year}-${month.padStart(2, '0')}-01`;
    const lastDateObj = new Date(year, monthNum, 0);
    const lastDay = formatDateLocal(lastDateObj);
    return { firstDay, lastDay };
  };

  useEffect(() => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const monthStr = `${year}-${month}`;
    setSelectedMonth(monthStr);
    const { firstDay, lastDay } = getMonthRange(monthStr);
    setFromDate(firstDay);
    setToDate(lastDay);
  }, []);

  useEffect(() => {
    if (FromDate && ToDate) {
      fetchPunchRecords();
    }
  }, [FromDate, ToDate]);

  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setSelectedMonth(newMonth);
    const { firstDay, lastDay } = getMonthRange(newMonth);
    setFromDate(firstDay);
    setToDate(lastDay);
  };

  const fetchPunchRecords = async () => {
    const dates = { FromDate, ToDate };
    
    setLoading(true);
    try {
      const result = await GetDailyAttendanceSummary(dates);
      setData(result.data?.data || []);
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCellColor = (approved, pending, total) => {
    if (total === 0) return 'bg-gray-200 border-gray-300';
    if (pending === 0) return 'bg-green-300 border-green-400 hover:bg-green-400';
    if (approved === 0) return 'bg-red-300 border-red-400 hover:bg-red-400';
    return 'bg-yellow-300 border-yellow-400 hover:bg-yellow-400';
  };

  const getStatusIcon = (approved, pending, total) => {
    if (total === 0) return null;
    if (pending === 0) return <span className="text-green-600 text-sm">✅</span>;
    if (approved === 0) return <span className="text-red-600 text-sm">⚠️</span>;
    return <span className="text-yellow-600 text-sm">⏰</span>;
  };

  const generateCalendar = () => {
    if (!selectedMonth) return [];
    const [year, month] = selectedMonth.split('-');
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const calendar = [];
    const weeks = [];
    let currentWeek = [];

    for (let i = 0; i < 42; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);


      const dateStr = formatDateLocal(currentDate);
      
      const dayData = Data.find((d) => formatDateLocal(new Date(d.Date)) === dateStr);

      const isCurrentMonth = currentDate.getMonth() === firstDay.getMonth();
      const isToday = dateStr === formatDateLocal(new Date());

      currentWeek.push({
        date: currentDate.getDate(),
        dateStr,
        data: dayData,
        isCurrentMonth,
        isToday,
      });

      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    return weeks;
  };

  const calendar = generateCalendar();
  const monthName = selectedMonth
    ? new Date(selectedMonth + '-01').toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      })
    : '';

  const totalApproved = Data.reduce((sum, day) => sum + day.Approved, 0);
  const totalPending = Data.reduce((sum, day) => sum + day.Pending, 0);
  const totalRecords = Data.reduce((sum, day) => sum + day.Total, 0);

  return (
    <div className="max-w-6xl mx-auto mt-6 p-6 bg-white rounded-2xl shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">📅</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-800">Attendance Calendar</h2>
        </div>
        <input
          type="month"
          value={selectedMonth}
          onChange={handleMonthChange}
          className="border border-gray-300 rounded-lg px-4 py-2"
        />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <SummaryCard icon="👥" label="Total Records" value={totalRecords} color="blue" />
        <SummaryCard icon="✅" label="Approved" value={totalApproved} color="green" />
        <SummaryCard icon="⚠️" label="Pending" value={totalPending} color="red" />
        <SummaryCard icon="📅" label="Month" value={monthName} color="purple" />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-6 mb-6 p-4 bg-gray-50 rounded-lg">
        <Legend color="green" label="All Approved" />
        <Legend color="yellow" label="Mixed Status" />
        <Legend color="red" label="All Pending" />
        {/* <Legend color="gray" label="No Records" /> */}
      </div>

      {/* Calendar */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Days Header */}
          <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div
                key={day}
                className="p-3 text-center font-semibold text-gray-700 text-sm"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Body */}
          <div className="grid grid-cols-7">
            {calendar.map((week, weekIndex) =>
              week.map((day, dayIndex) => (
                <div
                  key={`${weekIndex}-${dayIndex}`} onClick={()=>{handleViewMore(day)}}
                  className={`relative p-3 min-h-[120px] border-r border-b border-gray-400 transition-all duration-200
                    ${!day.isCurrentMonth ? 'opacity-40' : ''}
                    ${day.data ? getCellColor(day.data.Approved, day.data.Pending, day.data.Total) : 'bg-gray-200 border-gray-300'}
                    ${day.isToday ? 'ring-4 ring-blue-500 ring-opacity-70' : ''}`}
                >
                  <div className="flex flex-col items-center justify-center h-full">
                    <div
                      className={`text-3xl font-bold mb-2 ${
                        day.isCurrentMonth ? 'text-gray-900' : 'text-gray-500'
                      } ${day.isToday ? 'bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl' : ''}`}
                    >
                      {day.date}
                    </div>
                    <div className="mb-2">
                      {day.data && getStatusIcon(day.data.Approved, day.data.Pending, day.data.Total)}
                    </div>
                    {day.data && day.data.Total > 0 && (
                      <div className="text-center space-y-1">
                        <div className="text-xs font-semibold text-gray-800 bg-white bg-opacity-70 px-2 py-1 rounded">
                          Total: {day.data.Total}
                        </div>
                        <div className="flex justify-center space-x-2 text-xs">
                          <span className="bg-white bg-opacity-70 px-1 py-0.5 rounded text-green-800 font-medium">
                            ✓ {day.data.Approved}
                          </span>
                          <span className="bg-white bg-opacity-70 px-1 py-0.5 rounded text-red-800 font-medium">
                            ⏳ {day.data.Pending}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const SummaryCard = ({ icon, label, value, color }) => (
  <div className={`bg-gradient-to-r from-${color}-50 to-${color}-100 p-4 rounded-lg border border-${color}-200`}>
    <div className="flex items-center space-x-2">
      <span className={`text-${color}-600 text-lg`}>{icon}</span>
      <span className={`text-sm text-${color}-700 font-medium`}>{label}</span>
    </div>
    <p className={`text-2xl font-bold text-${color}-800`}>{value}</p>
  </div>
);

const Legend = ({ color, label }) => (
  <div className="flex items-center space-x-2">
    <div className={`w-4 h-4 bg-${color}-300 border border-${color}-400 rounded`}></div>
    <span className="text-sm text-gray-700">{label}</span>
  </div>
);

export default AttendanceCalendar;
